import { Map, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import { Box2 } from 'three';

import { CoordinateManager } from '../../../../../../viewer/coordinate-manager';
import { Position2 } from '../../../../../../viewer/report/manifest';
import { WmsState, WmsToolLayerItem } from '../../../../../../viewer/wms-state';
import { getWmsAttributions } from '../../../../wms-map/update-wms-attributions';
import { getWmsSource, MapLayers } from '../../../../wms-map/wms-map';

/** The largest side of the background map image, in pixels. */
const MAX_SIDE_PX = 4096;
const MM_PER_INCH = 25.4;
/** How long the map tiles may take to load. */
const RENDER_TIMEOUT_MS = 60 * 1000;

export type BackgroundMapImage = {
  png: Uint8Array;
  /** The north-up box the image covers, in the project's coordinate system. */
  extent: { min: Position2; max: Position2 };
  /** The attributions of the map's sources, as plain text. */
  attributions: string[];
};

/**
 * The pixel size of an image of the box on paper at the resolution, at most 4096 px a side.
 *
 * @param box In the project's coordinate system.
 */
export function getBackgroundMapSize(
  box: Box2,
  scale: number,
  metersPerUnit: number,
  dpi: number,
): { width: number; height: number } {
  const pixelsPerUnit = ((1000 * metersPerUnit) / scale / MM_PER_INCH) * dpi;
  const width = (box.max.x - box.min.x) * pixelsPerUnit;
  const height = (box.max.y - box.min.y) * pixelsPerUnit;
  const factor = Math.min(1, MAX_SIDE_PX / width, MAX_SIDE_PX / height);

  return {
    width: Math.max(1, Math.round(width * factor)),
    height: Math.max(1, Math.round(height * factor)),
  };
}

/** The map layer the viewer shows as the background map, if any. */
export function getShownMapLayer(): MapLayers | undefined {
  if (!WmsState.instance.wmsMapEnabled) {
    return undefined;
  }

  switch (WmsState.instance.selectedLayerItem) {
    case WmsToolLayerItem.Satellite:
      return MapLayers.Satellite;
    case WmsToolLayerItem.Road:
      return MapLayers.Road;
    default:
      return undefined;
  }
}

const toPlainText = (html: string) =>
  new DOMParser().parseFromString(html, 'text/html').body.textContent?.trim() ?? '';

/**
 * Renders the map layer for the box off screen, at the pixel size, as the viewer does: in the
 * map's projection, fitted to the box's corners. The extent of the image is the map's, converted
 * back the same way.
 *
 * @param box The north-up box in the project's coordinate system.
 */
async function renderBackgroundMap(
  mapLayer: MapLayers,
  box: Box2,
  size: { width: number; height: number },
): Promise<BackgroundMapImage> {
  const coordinates = CoordinateManager.getInstance();
  const projection = coordinates.getProjectProjection();
  const min = coordinates.convertCoords(box.min.x, box.min.y, projection, 'wms');
  const max = coordinates.convertCoords(box.max.x, box.max.y, projection, 'wms');

  const target = document.createElement('div');
  target.style.cssText = `position: fixed; left: -100000px; top: 0; width: ${size.width}px; height: ${size.height}px;`;
  document.body.appendChild(target);

  const source = getWmsSource(mapLayer);
  await source.initialize();
  const map = new Map({
    target,
    pixelRatio: 1,
    layers: [new TileLayer({ source: source.getSource() })],
    view: new View({ projection: WmsState.instance.viewerProjection }),
  });

  try {
    map.getView().fit([min.x, min.y, max.x, max.y], { size: [size.width, size.height] });

    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(
        () => reject(new Error('The map took too long.')),
        RENDER_TIMEOUT_MS,
      );
      map.once('rendercomplete', () => {
        clearTimeout(timeout);
        resolve();
      });
    });

    const canvas = map.getViewport().getElementsByTagName('canvas')[0];
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) {
      throw new Error('The map could not be read.');
    }

    const [x0, y0, x1, y1] = map.getView().calculateExtent([size.width, size.height]);
    const extentMin = coordinates.convertCoords(x0, y0, 'wms', projection);
    const extentMax = coordinates.convertCoords(x1, y1, 'wms', projection);

    return {
      png: new Uint8Array(await blob.arrayBuffer()),
      extent: { min: [extentMin.x, extentMin.y], max: [extentMax.x, extentMax.y] },
      attributions: getWmsAttributions(map)
        .map(toPlainText)
        .filter((text) => text.length > 0),
    };
  } finally {
    map.setTarget(undefined);
    map.dispose();
    target.remove();
  }
}

/**
 * Exported as a collection to allow for mocking: OpenLayers doesn't draw in tests.
 */
export const backgroundMapHelpers = { renderBackgroundMap };
